import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getTrainingPlan, getTrainingWeeks, getWeeklySessions, getPlanGoals } from '../../api/athlete';
import { useAuth } from '../../contexts/AuthContext';
import { TrainingPlan, TrainingWeek, WeeklySession, PlanGoal } from '../../types/athlete';
import PlanGoals from '../goals/PlanGoals';
import { Loader, Droplets, Bike, PersonStanding, Dumbbell } from 'lucide-react';

const TrainingMetrics: React.FC = () => {
  const { id: planId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [plan, setPlan] = useState<TrainingPlan | null>(null);
  const [weeks, setWeeks] = useState<TrainingWeek[]>([]);
  const [sessions, setSessions] = useState<Record<string, WeeklySession[]>>({});
  const [goals, setGoals] = useState<PlanGoal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!planId || !user) return;
    let isMounted = true;
    const loadData = async () => {
      try {
        const planData = await getTrainingPlan(user.id, planId);
        if (isMounted) setPlan(planData);

        const weeksData = await getTrainingWeeks(user.id, planId);
        if (isMounted) setWeeks(weeksData);

        const goalsData = await getPlanGoals(planId);
        if (isMounted) setGoals(goalsData);

        const sessionsByWeek: Record<string, WeeklySession[]> = {};
        for (const week of weeksData) {
          const weekSessions = await getWeeklySessions(user.id, week.id);
          sessionsByWeek[week.id] = weekSessions;
        }
        if (isMounted) setSessions(sessionsByWeek);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [planId, user]);

  const allSessions = Object.values(sessions).flat();
  const completedCount = allSessions.filter(s => s.completed).length;
  const totalCount = allSessions.length;
  const completionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const totalPlannedMinutes = allSessions.reduce((sum, s) => sum + (s.planned_duration_minutes || 0), 0);
  const totalActualMinutes = allSessions.filter(s => s.completed).reduce((sum, s) => sum + (s.actual_duration_minutes || 0), 0);

  const formatMinutes = (mins: number): string => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin" /></div>;
  }

  if (!plan) {
    return <div className="text-center py-12">Plan not found</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">{plan.name}</h1>
          <p className="text-muted">Training metrics and progress</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card-base p-6">
          <p className="text-sm text-muted mb-1">Completion</p>
          <p className="text-3xl font-bold text-[#3b82f6]">{completionRate}%</p>
          <p className="text-xs text-muted">{completedCount}/{totalCount} sessions</p>
        </div>
        <div className="card-base p-6">
          <p className="text-sm text-muted mb-1">Planned Time</p>
          <p className="text-3xl font-bold">{formatMinutes(totalPlannedMinutes)}</p>
        </div>
        <div className="card-base p-6">
          <p className="text-sm text-muted mb-1">Actual Time</p>
          <p className="text-3xl font-bold text-green-400">{formatMinutes(totalActualMinutes)}</p>
        </div>
        <div className="card-base p-6">
          <p className="text-sm text-muted mb-1">Weeks</p>
          <p className="text-3xl font-bold">{weeks.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-base p-6">
          <h2 className="font-semibold mb-4">Goals Progress</h2>
          <PlanGoals
            planId={planId!}
            goals={goals}
            onUpdate={async () => {
              const goalsData = await getPlanGoals(planId!);
              setGoals(goalsData);
            }}
          />
        </div>

        <div className="card-base p-6">
          <h2 className="font-semibold mb-4">Discipline Breakdown</h2>
          {['swim', 'bike', 'run', 'strength'].map((disc) => {
            const discSessions = allSessions.filter(s => s.session_type?.discipline === disc);
            const discCompleted = discSessions.filter(s => s.completed);
            const discPlanned = discSessions.length;
            const discActual = discCompleted.reduce((sum, s) => sum + (s.actual_duration_minutes || 0), 0);
            const discRate = discPlanned > 0 ? Math.round((discCompleted.length / discPlanned) * 100) : 0;

            return (
              <div key={disc} className="mb-4 last:mb-0">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    {disc === 'swim' && <Droplets size={16} className="text-blue-400" />}
                    {disc === 'bike' && <Bike size={16} className="text-green-400" />}
                    {disc === 'run' && <PersonStanding size={16} className="text-orange-400" />}
                    {disc === 'strength' && <Dumbbell size={16} className="text-purple-400" />}
                    <span className="capitalize">{disc}</span>
                  </div>
                  <span className="text-sm text-muted">{discRate}%</span>
                </div>
                <div className="h-2 bg-[#141921] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      disc === 'swim' ? 'bg-blue-500' :
                      disc === 'bike' ? 'bg-green-500' :
                      disc === 'run' ? 'bg-orange-500' :
                      'bg-purple-500'
                    }`}
                    style={{ width: `${discRate}%` }}
                  />
                </div>
                <p className="text-xs text-muted mt-1">
                  {discCompleted.length}/{discPlanned} sessions • {formatMinutes(discActual)} actual
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TrainingMetrics;
